/**
 * WMS1131_R01(사급자재 입고 - 자동 LOT) 품목 라벨 빌더
 *
 * WMS1130과 달리 사용자가 LOT을 직접 관리하지 않고, 저장 시 LOT가 자동 생성된다.
 * 라벨은 LOT 번호가 아닌 품목코드(itemCd) 바코드로 출력한다.
 */
import { ItemLabelPrintData } from "src/apis/itemLabelApi";
import { formatDate } from "src/utils/string/stringUtil";
import { WMS1131_R01DetailDto, WMS1131_R01HeaderDto } from "./Dto_WMS1131_R01";

export interface WMS1131_R01StoreData {
	WMS1131_R01DetailList: WMS1131_R01DetailDto[];
	editWMS1131_R01: WMS1131_R01HeaderDto | null;
}

/** 수량 포맷 (콤마 + 단위) */
const formatQty = (qty: number | undefined, unit: string): string => {
	if (!qty && qty !== 0) return '';
	return `${qty.toLocaleString()}${unit || 'EA'}`;
};

/** 품목 1건 → ItemLabelPrintData 1건 변환 */
export const WMS1131_R01_buildItemLabelData = (
	header: WMS1131_R01HeaderDto,
	detail: WMS1131_R01DetailDto,
): ItemLabelPrintData => {
	if (!header) throw new Error('입고 정보를 찾을 수 없습니다.');
	if (!detail?.itemCd) throw new Error('품목코드를 찾을 수 없습니다.');
	if ((detail.qty || 0) <= 0) throw new Error('입고수량이 없어 라벨을 생성할 수 없습니다.');

	const transportDate = header.transportDt
		? formatDate(new Date(header.transportDt))
		: formatDate(new Date());

	return {
		itemCd: detail.itemCd || '',
		itemNm: detail.itemNm || '',
		itemSpec: detail.itemSpec || '',
		qty: formatQty(detail.qty, detail.unit || 'EA'),
		transportDate,
		storageLocation: detail.locationNm || detail.locationCd || header.warehouseNm || '',
		customer: header.customNm || '',
		customerItemCd: detail.itemCd || '',
		labelType: '[자재입고]',
	};
};

export const WMS1131_R01_buildItemLabelDataByValueIndex = (
	valueIndex: number,
	store: WMS1131_R01StoreData,
): ItemLabelPrintData | null => {
	const detail = store.WMS1131_R01DetailList.find((d) => d.valueIndex === valueIndex);
	if (!detail || !store.editWMS1131_R01) {
		console.warn(`Detail를 찾을 수 없습니다: valueIndex=${valueIndex}`);
		return null;
	}

	try {
		return WMS1131_R01_buildItemLabelData(store.editWMS1131_R01, detail);
	} catch (error) {
		console.error('품목 라벨 데이터 생성 실패:', error);
		return null;
	}
};

/**
 * 저장 핸들러에서 호출. Lot라벨자동출력=Y일 때만 사용.
 * 삭제 대상 제외·미출력·수량>0 인 품목만 골라 ItemLabelPrintData[] 반환.
 */
export const WMS1131_R01_extractUnprintedItemLabelData = (
	store: WMS1131_R01StoreData,
	deletedDetailList: string[] = [],
): ItemLabelPrintData[] => {
	const { WMS1131_R01DetailList, editWMS1131_R01 } = store;
	if (!editWMS1131_R01) return [];

	const unprintedLabelData: ItemLabelPrintData[] = [];
	const deletedSet = new Set(deletedDetailList);

	WMS1131_R01DetailList.forEach((detail) => {
		const valueIndex = detail.valueIndex?.toString() ?? '';
		if (
			!deletedSet.has(valueIndex) &&
			!detail.printed &&
			(detail.qty || 0) > 0
		) {
			try {
				unprintedLabelData.push(WMS1131_R01_buildItemLabelData(editWMS1131_R01, detail));
			} catch (error) {
				console.error(`품목 라벨 데이터 구성 실패: ${detail.itemCd}`, error);
			}
		}
	});

	return unprintedLabelData;
};
